import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { AgGrid, AgGridReact, toast } from "@jsq/ds/core";
import {
  useCreateSavedViewMutation,
  useGetSavedViewQuery,
  useGetFilterOptionsQuery,
  useGetFieldMetadataQuery,
} from "#graphql/supergraphHooks";
import { type GetSavedViewQuery } from "#graphql/supergraphTypes";
import { ControlTowerContext } from "#utils/context";
import { JIRA_PROJECT_KEY } from "../../constants";
import { useControlTowerContext } from "../../ControlTowerContext";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import { BUILT_IN_JIRA_FIELDS } from "../constants/fieldMetadata";
import { FilterOptionsMap, FilterOptionsWithMapping } from "../types";

type ColumnDefs = AgGridReact.AgGridReactProps["columnDefs"];

type SavableGridConfiguration = Pick<
  AgGrid.GridState,
  "columnVisibility" | "columnSizing" | "columnOrder" | "filter" | "sort"
>;

interface FilterParamsWithValues {
  values?: string[];
}

const hasFilterValues = (
  filterParams: AgGrid.ColDef["filterParams"],
): filterParams is FilterParamsWithValues => {
  if (!filterParams || typeof filterParams !== "object") return false;
  if (!("values" in filterParams)) return false;

  const params = filterParams as Record<string, unknown>;
  const valuesProperty = params["values"];
  return Array.isArray(valuesProperty) && valuesProperty.length > 0;
};

interface UseGridViewManagerProps {
  getColumnDefs: (filterOptions: FilterOptionsMap) => ColumnDefs;
  gridApi?: AgGrid.GridApi;
  onSavedViewFilterApplied?: (filterModel: AgGrid.FilterModel) => void;
}

interface UseGridViewManagerReturn {
  columnDefs: ColumnDefs | null;
  shouldShowGrid: boolean;
  saveViewLoading: boolean;
  handleSavedViewClicked: (api: AgGrid.GridApi) => Promise<void>;
  hasSavedView: boolean;
  savedConfiguration: SavableGridConfiguration | null;
  fieldMetadataMap: Map<string, string> | null;
  filterOptions: FilterOptionsMap;
  hasUrlFilters: boolean;
  updateUrlFilters: (filterModel: AgGrid.FilterModel | null) => void;
  updateUrlSort: (sortModel: AgGrid.SortModelItem[] | null) => void;
  /** Clear all URL filter params */
  clearUrlFilters: () => void;
}

export const useGridViewManager = ({
  getColumnDefs,
  gridApi,
  onSavedViewFilterApplied,
}: UseGridViewManagerProps): UseGridViewManagerReturn => {
  const context = useControlTowerContext();
  const isGPX = context === ControlTowerContext.GPX;
  const [shouldShowGrid, setShouldShowGrid] = useState(false);
  const [columnDefs, setColumnDefs] = useState<ColumnDefs | null>(null);
  const [savedFilterModelFromApi, setSavedFilterModelFromApi] =
    useState<AgGrid.FilterModel | null>(null);
  const [savedSortModelFromApi, setSavedSortModelFromApi] = useState<
    AgGrid.SortModelItem[] | null
  >(null);
  const [savedConfig, setSavedConfig] = useState<SavableGridConfiguration | null>(null);
  const [hasSavedView, setHasSavedView] = useState(false);
  const appliedColumnFieldsRef = useRef<string[]>([]);
  const hasInitializedForGPX = useRef(false);
  const hasAppliedUrlFilters = useRef(false);
  const hasAppliedUrlSort = useRef(false);

  const {
    urlFilterModel,
    urlSortModel,
    hasUrlFilters,
    updateUrlFilters,
    updateUrlSort,
    clearUrlFilters,
  } = useUrlFilters();

  const hadInitialUrlFilters = useRef(hasUrlFilters);

  const { data: filterOptionsData, loading: filterOptionsLoading } = useGetFilterOptionsQuery({
    variables: { projectKey: JIRA_PROJECT_KEY },
  });

  const { data: fieldMetadataData, loading: fieldMetadataLoading } = useGetFieldMetadataQuery({
    variables: { projectKey: JIRA_PROJECT_KEY },
  });

  const isDataLoaded = !filterOptionsLoading && !fieldMetadataLoading;

  const fieldMetadataMap = useMemo(() => {
    if (!isDataLoaded || !fieldMetadataData?.tickets?.fieldMetadata) {
      return null;
    }
    const metadataMap = new Map<string, string>();

    fieldMetadataData.tickets.fieldMetadata.forEach((metadata) => {
      const fieldId = BUILT_IN_JIRA_FIELDS.has(metadata.fieldName)
        ? metadata.fieldName
        : metadata.jiraFieldId;
      metadataMap.set(metadata.fieldName, fieldId);
    });
    return metadataMap;
  }, [isDataLoaded, fieldMetadataData]);

  const filterOptions = useMemo((): FilterOptionsMap => {
    if (!isDataLoaded || !filterOptionsData?.filterOptions) {
      return {};
    }
    const options = filterOptionsData.filterOptions;

    const buildFilterOptionsWithMapping = (
      opts: Array<{ value: string; displayName?: string | null }> | null | undefined,
    ): FilterOptionsWithMapping => {
      if (!opts) {
        return { displayValues: [], displayToValueMap: {} };
      }

      const displayToValueMap: Record<string, string> = {};
      const displayValues: string[] = [];
      const displayNameCounts = new Map<string, number>();

      opts.forEach((opt) => {
        const displayName = opt.displayName || opt.value;
        displayNameCounts.set(displayName, (displayNameCounts.get(displayName) || 0) + 1);
      });

      opts.forEach((opt) => {
        const displayName = opt.displayName || opt.value;
        const count = displayNameCounts.get(displayName) || 0;
        const finalDisplayName = count > 1 ? `${displayName} (${opt.value})` : displayName;
        displayValues.push(finalDisplayName);
        displayToValueMap[finalDisplayName] = opt.value;
      });

      return { displayValues, displayToValueMap };
    };

    return {
      workType: options.issuetype?.map((opt) => opt.value) || [],
      entityName: options.entityName?.map((opt) => opt.value) || [],
      assignee: buildFilterOptionsWithMapping(options.assignee),
      faPreparer: buildFilterOptionsWithMapping(options.faPreparer),
      faReviewer: buildFilterOptionsWithMapping(options.faReviewer),
      isPrimary: buildFilterOptionsWithMapping(options.isPrimary),
      isReviewer: buildFilterOptionsWithMapping(options.isReviewer),
      status: options.status?.map((opt) => opt.value) || [],
      customers: options.customers?.map((opt) => opt.value) || [],
    };
  }, [isDataLoaded, filterOptionsData]);

  const defaultColumnDefs = useMemo(() => {
    if (!isDataLoaded) return [];
    return getColumnDefs(filterOptions);
  }, [isDataLoaded, getColumnDefs, filterOptions]);

  const hasFilterOptionsLoaded = useCallback((colDefs: ColumnDefs): boolean => {
    if (!colDefs) return false;

    return colDefs.some((colDef) => {
      if (!("filterParams" in colDef)) return false;
      return hasFilterValues(colDef.filterParams);
    });
  }, []);

  const applyVisibilityAndSizing = useCallback(
    (
      colDef: AgGrid.ColDef | AgGrid.ColGroupDef,
      columnVisibility?: SavableGridConfiguration["columnVisibility"],
      columnSizing?: SavableGridConfiguration["columnSizing"],
    ) => {
      if (!("field" in colDef) || !colDef.field) return colDef;

      const colId = colDef.field;
      const updatedColDef = { ...colDef };

      if ("hide" in updatedColDef && columnVisibility?.hiddenColIds) {
        updatedColDef.hide = columnVisibility.hiddenColIds.includes(colId);
      }

      const savedWidth = columnSizing?.columnSizingModel?.find(
        (col: { colId: string; width?: number }) => col.colId === colId,
      )?.width;
      if (savedWidth && "width" in updatedColDef) {
        updatedColDef.width = savedWidth;
      }

      if ("sort" in updatedColDef) {
        delete updatedColDef.sort;
      }

      return updatedColDef;
    },
    [],
  );

  const sortColumnsByOrder = useCallback(
    (
      columnDefs: ColumnDefs,
      columnOrder?: SavableGridConfiguration["columnOrder"],
    ): ColumnDefs => {
      if (!columnOrder?.orderedColIds?.length || !columnDefs) {
        return columnDefs;
      }

      return [...columnDefs].sort((a, b) => {
        const aField = "field" in a ? a.field : undefined;
        const bField = "field" in b ? b.field : undefined;
        const aIndex = aField ? columnOrder.orderedColIds.indexOf(aField) : -1;
        const bIndex = bField ? columnOrder.orderedColIds.indexOf(bField) : -1;
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    },
    [],
  );

  const applyConfigToColumnDefs = useCallback(
    (config: SavableGridConfiguration | null, baseDefs: ColumnDefs) => {
      if (!config || !baseDefs) {
        return baseDefs;
      }

      const { columnVisibility, columnOrder, columnSizing } = config;

      const defsWithVisibilityAndSizing = baseDefs.map((colDef) =>
        applyVisibilityAndSizing(colDef, columnVisibility, columnSizing),
      );

      return sortColumnsByOrder(defsWithVisibilityAndSizing, columnOrder);
    },
    [applyVisibilityAndSizing, sortColumnsByOrder],
  );

  const isSavableGridConfiguration = (
    config: NonNullable<GetSavedViewQuery["getSavedView"]>["configuration"] | undefined,
  ): config is SavableGridConfiguration => {
    if (!config || typeof config !== "object") return false;
    return true;
  };

  const handleGetSavedViewCompleted = (data: GetSavedViewQuery) => {
    const rawConfig = data.getSavedView?.configuration;

    if (!isSavableGridConfiguration(rawConfig) || !defaultColumnDefs) {
      setColumnDefs(defaultColumnDefs);
      setShouldShowGrid(true);
      setHasSavedView(false);
      return;
    }

    const { filter, sort } = rawConfig;

    setSavedConfig(rawConfig);
    setSavedFilterModelFromApi(filter?.filterModel ?? null);
    setSavedSortModelFromApi(sort?.sortModel ?? null);
    setColumnDefs(applyConfigToColumnDefs(rawConfig, defaultColumnDefs));
    setShouldShowGrid(true);
    setHasSavedView(true);
  };

  useEffect(() => {
    if (!shouldShowGrid || !defaultColumnDefs || defaultColumnDefs.length === 0) return;

    const currentFields = defaultColumnDefs
      .filter((col): col is AgGrid.ColDef => "field" in col && !!col.field)
      .map((col) => col.field as string);

    const hasNewColumns = currentFields.some(
      (field) => !appliedColumnFieldsRef.current.includes(field),
    );
    const isFirstApplication = appliedColumnFieldsRef.current.length === 0;

    if (isFirstApplication || hasNewColumns) {
      if (savedConfig && !hasFilterOptionsLoaded(defaultColumnDefs)) {
        return;
      }

      setColumnDefs(applyConfigToColumnDefs(savedConfig, defaultColumnDefs));
      appliedColumnFieldsRef.current = currentFields;
    }
  }, [
    defaultColumnDefs,
    savedConfig,
    shouldShowGrid,
    applyConfigToColumnDefs,
    hasFilterOptionsLoaded,
  ]);

  useEffect(() => {
    if (
      gridApi &&
      hadInitialUrlFilters.current &&
      urlFilterModel &&
      !hasAppliedUrlFilters.current
    ) {
      gridApi.setFilterModel(urlFilterModel);
      hasAppliedUrlFilters.current = true;
      setSavedFilterModelFromApi(null);
    }
  }, [gridApi, urlFilterModel]);

  useEffect(() => {
    if (hadInitialUrlFilters.current) {
      return;
    }

    if (gridApi && savedFilterModelFromApi && Object.keys(savedFilterModelFromApi).length > 0) {
      gridApi.setFilterModel(savedFilterModelFromApi);
      onSavedViewFilterApplied?.(savedFilterModelFromApi);
      setSavedFilterModelFromApi(null);
    }
  }, [gridApi, savedFilterModelFromApi, onSavedViewFilterApplied]);

  useEffect(() => {
    if (urlSortModel && urlSortModel.length > 0) {
      setSavedSortModelFromApi(null);
      return;
    }
    if (gridApi && savedSortModelFromApi && savedSortModelFromApi.length > 0) {
      gridApi.applyColumnState({
        state: savedSortModelFromApi.map((sortItem) => ({
          colId: sortItem.colId,
          sort: sortItem.sort,
        })),
      });
      setSavedSortModelFromApi(null);
    }
  }, [gridApi, savedSortModelFromApi, urlSortModel]);

  useEffect(() => {
    if (gridApi && urlSortModel && urlSortModel.length > 0 && !hasAppliedUrlSort.current) {
      gridApi.applyColumnState({
        defaultState: { sort: null },
        state: urlSortModel.map((sortItem) => ({
          colId: sortItem.colId,
          sort: sortItem.sort,
        })),
      });
      hasAppliedUrlSort.current = true;
    }
  }, [gridApi, urlSortModel]);

  const handleGetSavedViewError = () => {
    toast.error("Failed to load saved view");
    setColumnDefs(defaultColumnDefs);
    setShouldShowGrid(true);
    setHasSavedView(false);
  };

  useGetSavedViewQuery({
    onCompleted: handleGetSavedViewCompleted,
    onError: handleGetSavedViewError,
    fetchPolicy: "no-cache",
    skip: !isDataLoaded || isGPX,
  });

  useEffect(() => {
    if (isGPX && isDataLoaded && !hasInitializedForGPX.current) {
      hasInitializedForGPX.current = true;
      setColumnDefs(defaultColumnDefs);
      setShouldShowGrid(true);
    }
  }, [isGPX, isDataLoaded, defaultColumnDefs]);

  const [createSavedView, { loading: saveViewLoading }] = useCreateSavedViewMutation({
    onCompleted: () => {
      toast.success("View saved successfully");
    },
    onError: () => {
      toast.error("Failed to save view");
    },
  });

  const buildColumnVisibility = useCallback(
    (columnState: AgGrid.ColumnState[]): SavableGridConfiguration["columnVisibility"] => {
      const hiddenColIds = columnState.filter((col) => col.hide).map((col) => col.colId);
      return { hiddenColIds };
    },
    [],
  );

  const buildColumnSizing = useCallback(
    (columnState: AgGrid.ColumnState[]): SavableGridConfiguration["columnSizing"] => {
      const columnSizingModel = columnState
        .filter((col) => col.width !== undefined)
        .map((col) => ({ colId: col.colId, width: col.width }));
      return { columnSizingModel };
    },
    [],
  );

  const buildColumnOrder = useCallback(
    (columnState: AgGrid.ColumnState[]): SavableGridConfiguration["columnOrder"] => {
      const orderedColIds = columnState.map((col) => col.colId);
      return { orderedColIds };
    },
    [],
  );

  const buildSort = useCallback(
    (columnState: AgGrid.ColumnState[]): SavableGridConfiguration["sort"] | undefined => {
      const sortModel = columnState
        .filter((col): col is AgGrid.ColumnState & { sort: "asc" | "desc" } =>
          Boolean(col.sort),
        )
        .map((col) => ({ colId: col.colId, sort: col.sort }));
      return sortModel.length > 0 ? { sortModel } : undefined;
    },
    [],
  );

  const buildSavedViewConfiguration = useCallback(
    (api: AgGrid.GridApi): SavableGridConfiguration => {
      const columnState = api.getColumnState();
      const filterModel = api.getFilterModel();

      const columnVisibility = buildColumnVisibility(columnState);
      const columnSizing = buildColumnSizing(columnState);
      const columnOrder = buildColumnOrder(columnState);
      const filter = { filterModel };
      const sort = buildSort(columnState);

      return {
        columnVisibility,
        columnSizing,
        columnOrder,
        filter,
        ...(sort && { sort }),
      };
    },
    [buildColumnVisibility, buildColumnSizing, buildColumnOrder, buildSort],
  );

  const handleSavedViewClicked = useCallback(
    async (api: AgGrid.GridApi) => {
      const configuration = buildSavedViewConfiguration(api);
      await createSavedView({ variables: { input: { configuration } } });
    },
    [createSavedView, buildSavedViewConfiguration],
  );

  return {
    columnDefs,
    shouldShowGrid,
    saveViewLoading,
    handleSavedViewClicked,
    hasSavedView,
    savedConfiguration: savedConfig,
    fieldMetadataMap,
    filterOptions,
    hasUrlFilters,
    updateUrlFilters,
    updateUrlSort,
    clearUrlFilters,
  };
};

export type {
  UseGridViewManagerProps,
  UseGridViewManagerReturn,
  ColumnDefs,
  SavableGridConfiguration,
};
